import React from 'react';
import { useSelector } from 'react-redux';
import Axios from 'axios';
import ReCAPTCHA from 'react-google-recaptcha';

// @material-ui/core components
import { makeStyles } from '@mui/styles';
import Snackbar from '@material-ui/core/Snackbar';
// @material-ui/icons
import {PinDrop} from '@material-ui/icons';
import {Phone} from '@material-ui/icons';
// core components
import GridContainer from 'componentsPage/Grid/GridContainer.js';
import GridItem from 'componentsPage/Grid/GridItem.js';
import InfoArea from 'componentsPage/InfoArea/InfoArea.js';
import Card from 'componentsPage/Card/Card.js';
import CardHeader from 'componentsPage/Card/CardHeader.js';
import CardBody from 'componentsPage/Card/CardBody.js';
import CardFooter from 'componentsPage/Card/CardFooter.js';
import CustomInput from 'componentsPage/CustomInput/CustomInput.js';
import Button from 'componentsPage/CustomButtons/Button.js';
import contactsStyle from 'assets/jss/material-kit-pro-react/views/sectionsSections/contactsStyle.js';

import city from 'assets/img/examples/office5.jpg';

const useStyles = makeStyles(contactsStyle);

const NOMBRE_COMPLETO_REGEX =
  /^[A-Za-zÁÉÍÓÚÜÑáéíóúüñ]+(?:['’-][A-Za-zÁÉÍÓÚÜÑáéíóúüñ]+)*(?:\s+[A-Za-zÁÉÍÓÚÜÑáéíóúüñ]+(?:['’-][A-Za-zÁÉÍÓÚÜÑáéíóúüñ]+)*)+$/;
const CORREO_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const RECAPTCHA_SITE_KEY = '6LfGC04qAAAAAG7UQBpNpmDiSWbBEkxWNidCkH0D';
const MINIMO_CARACTERES_MENSAJE = 30;
const MINIMO_PALABRAS_MENSAJE = 5;

const obtenerErrorMensaje = (texto) => {
  const textoLimpio = texto.trim();
  const cantidadCaracteres = textoLimpio.length;
  const cantidadPalabras = textoLimpio ? textoLimpio.split(/\s+/).length : 0;
  const caracteresFaltantes = Math.max(0, MINIMO_CARACTERES_MENSAJE - cantidadCaracteres);
  const palabrasFaltantes = Math.max(0, MINIMO_PALABRAS_MENSAJE - cantidadPalabras);
  const requisitosFaltantes = [];

  if (caracteresFaltantes > 0) {
    requisitosFaltantes.push(
      `${caracteresFaltantes} ${caracteresFaltantes === 1 ? 'carácter' : 'caracteres'}`
    );
  }
  if (palabrasFaltantes > 0) {
    requisitosFaltantes.push(
      `${palabrasFaltantes} ${palabrasFaltantes === 1 ? 'palabra' : 'palabras'}`
    );
  }

  return requisitosFaltantes.length > 0
    ? `El mensaje requiere ${requisitosFaltantes.join(' y ')} más.`
    : '';
};

export default function SectionContacts({ ...rest }) {
  const [nombres, setNombres] = React.useState('');
  const [apellidos, setApellidos] = React.useState('');
  const [correo, setCorreo] = React.useState('');
  const [mensaje, setMensaje] = React.useState('');
  const [captchaToken, setCaptchaToken] = React.useState(null);
  const [errorEmail, setErrorEmail] = React.useState({ mostrar: false, tipo: 'warning', message: '' });
  const recaptchaRef = React.useRef(null);

  const notaria = useSelector((state) => state.notaria.notaria);

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setErrorEmail({ mostrar: false, tipo: 'warning', message: '' });
  };

  const enviarCorreo = async (e) => {
    e.preventDefault();

    const nombreLimpio = `${nombres.trim()} ${apellidos.trim()}`.replace(/\s+/g, ' ').trim();
    const correoLimpio = correo.trim();
    const mensajeLimpio = mensaje.trim();

    if (!NOMBRE_COMPLETO_REGEX.test(nombreLimpio)) {
      setErrorEmail({
        mostrar: true,
        tipo: 'danger',
        message: 'Escriba un nombre completo con al menos dos palabras.',
      });
      return;
    }
    if (!CORREO_REGEX.test(correoLimpio)) {
      setErrorEmail({
        mostrar: true,
        tipo: 'danger',
        message: 'Escriba un correo electrónico válido.',
      });
      return;
    }

    const errorMensaje = obtenerErrorMensaje(mensajeLimpio);
    if (errorMensaje) {
      setErrorEmail({ mostrar: true, tipo: 'danger', message: errorMensaje });
      return;
    }
    if (!captchaToken) {
      setErrorEmail({
        mostrar: true,
        tipo: 'danger',
        message: 'Debe completar la verificación “No soy un robot”.',
      });
      return;
    }

    try {
      let config = {
        method: 'POST',
        mode: 'cors',
        headers: { 'Access-Control-Allow-Origin': true, Authorization: 'Bearer ' + process.env.REACT_APP_TOKEN_API },
      };
      let data = {
        remitente_name: nombreLimpio,
        remitente_email: correoLimpio,
        descripcion: mensajeLimpio,
        destinatario: notaria.email_contactenos,
      };
      const resultado = await Axios.post(process.env.REACT_APP_UrlApi + 'MailContact', data, config);

      if (resultado.status === 200) {
        setNombres('');
        setApellidos('');
        setCorreo('');
        setMensaje('');
        setCaptchaToken(null);
        recaptchaRef.current?.reset();
        setErrorEmail({ mostrar: true, tipo: 'success', message: 'Correo Enviado, pronto le daremos una respuesta' });
      } else {
        setErrorEmail({ mostrar: true, tipo: 'warning', message: 'Sucedió un error, intentarlo mas tarde' });
      }
    } catch (eroror) {
      setErrorEmail({ mostrar: true, tipo: 'warning', message: 'Sucedió un error, intentarlo mas tarde' });
    }
  };
  const classes = useStyles();

  return (
    <div className="cd-section" {...rest}>
      {/* Contact us 1 START */}
      <div className={classes.contacts + ' ' + classes.section} style={{ backgroundImage: `url(${city})` }}>
        <div className={classes.container}>
          <GridContainer>
            <GridItem xs={12} sm={8} md={8}>
              <h2 className={classes.title}>Información de contacto</h2>
              <h5 className={classes.description}>
                Necesita más información? Visite nuestras oficinas o comuníquese con nuestras líneas de atención al
                cliente para recibir información adicional.
              </h5>
              <GridContainer>
                <GridItem xs={12} sm={12} md={12}>
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d989.7965221814653!2d-73.12388111192017!3d7.104420230486248!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8e683fc89b0e4729%3A0x9b61e03995ceb341!2sNotar%C3%ADa%2011%20de%20Bucaramanga!5e0!3m2!1ses!2sco!4v1659725148767!5m2!1ses!2sco"
                    width="100%"
                    height="100%"
                    frameBorder="0"
                    allowFullScreen=""
                    aria-hidden="false"
                    tabIndex="0"
                  ></iframe>
                </GridItem>
              </GridContainer>
              <GridContainer>
                <GridItem xs={12} sm={6} md={6}>
                  <InfoArea
                    className={classes.infoArea}
                    title="Estamos ubicados"
                    description={
                      <span className={classes.textWhite}>
                        Direccion: Avenida los Samanes No 9-35 Barrio Real de Minas Bucaramanga, Santander
                        <br /> Lunes a Jueves: 7:45am a 12:30pm y de 2:00pm a 5:30pm
                        <br /> Viernes: 7:30am a 12:30pm y de 1:30pm a 5:30pm
                        <br /><b>Horario</b>
                        <br /> Sabados de Turno: 8:00am a 12:00pm
                        <br /> Agosto 22
                        <br /> Septiembre 26
                        <br /> Octubre 31
                        <br /> Diciembre 5
                      </span>
                    }
                    icon={PinDrop}
                  />
                </GridItem>
                <GridItem xs={12} sm={6} md={6}>
                  <InfoArea
                    className={classes.infoArea}
                    title="Llaménos"
                    description={
                      <span className={classes.textWhite}>
                        Teléfonos
                        <br />
                        607 6985455
                        <br />
                        +57 3002948002
                      </span>
                    }
                    icon={Phone}
                  />
                </GridItem>
              </GridContainer>
            </GridItem>
            <GridItem xs={12} sm={4} md={4} className={classes.mlAuto}>
              <Card className={classes.card1}>
                <form onSubmit={enviarCorreo}>
                  <CardHeader contact color="danger" className={classes.textCenter}>
                    <h4 className={classes.cardTitle}>Contáctenos por Correo electrónico</h4>
                  </CardHeader>
                  <CardBody className={classes.contactFormBody}>
                    <GridContainer className={classes.contactNameRow}>
                      <GridItem xs={12} sm={6} md={6}>
                        <CustomInput
                          labelText="Nombres"
                          id="first"
                          formControlProps={{
                            fullWidth: true,
                            className: classes.contactField,
                          }}
                          inputProps={{
                            value: nombres,
                            onChange: (e) => setNombres(e.target.value),
                            type: 'text',
                          }}
                        />
                      </GridItem>
                      <GridItem xs={12} sm={6} md={6}>
                        <CustomInput
                          labelText="Apellidos"
                          id="last"
                          formControlProps={{
                            fullWidth: true,
                            className: classes.contactField,
                          }}
                          inputProps={{
                            value: apellidos,
                            onChange: (e) => setApellidos(e.target.value),
                            type: 'text',
                          }}
                        />
                      </GridItem>
                    </GridContainer>
                    <CustomInput
                      labelText="Correo electrónico"
                      id="email-address"
                      formControlProps={{
                        fullWidth: true,
                        className: classes.contactField,
                      }}
                      inputProps={{
                        value: correo,
                        onChange: (e) => setCorreo(e.target.value),
                        type: 'email',
                      }}
                    />
                    <CustomInput
                      labelText="Mensaje"
                      id="message"
                      formControlProps={{
                        fullWidth: true,
                        className: classes.contactMessageField,
                      }}
                      inputProps={{
                        value: mensaje,
                        multiline: true,
                        rows: 3,
                        onChange: (e) => setMensaje(e.target.value),
                        type: 'text',
                      }}
                    />
                    <div className={classes.contactCaptcha}>
                      <ReCAPTCHA
                        ref={recaptchaRef}
                        sitekey={RECAPTCHA_SITE_KEY}
                        onChange={setCaptchaToken}
                        onExpired={() => setCaptchaToken(null)}
                      />
                    </div>
                    {errorEmail.mostrar && (
                      <p
                        style={{
                          color: errorEmail.tipo === 'success' ? '#4caf50' : '#f44336',
                          fontSize: 13,
                          marginTop: 12,
                          marginBottom: 0,
                        }}
                      >
                        {errorEmail.message}
                      </p>
                    )}
                  </CardBody>
                  <CardFooter className={classes.justifyContentBetween}>
                    <Snackbar
                      open={errorEmail.mostrar}
                      autoHideDuration={6000}
                      onClose={handleClose}
                      message={errorEmail.message}
                    />
                    <Button color="danger" className={classes.pullRight} type="submit">
                      Enviar Mensaje
                    </Button>
                  </CardFooter>
                </form>
              </Card>
            </GridItem>
          </GridContainer>
          <GridContainer className="ml-auto mr-auto">
            <GridItem xs={12} sm={8} md={12}>
              <h4 className="mb-4 text-white text-center" style={{ fontWeight: 500 }}>
                Atención al Público
              </h4>
            </GridItem>
            <GridItem xs={12} sm={8} md={4} className="text-center"></GridItem>
            <GridItem xs={12} sm={8} md={4} className="text-center">
              <a
                type="button"
                className="btn btn-rotate  btn-block btn-round mr-1 "
                style={{ backgroundColor: '#ec3642', color: 'white', borderColor: '#ec3642' }}
                href="/page/pqrs"
                onClick={(e) => e.preventDefault}
              >
                <i className="fa fa-hand-pointer-o" aria-hidden="true"></i>
                PQRS
              </a>
            </GridItem>
            <GridItem xs={12} sm={8} md={4} className="text-center"></GridItem>
          </GridContainer>
        </div>
      </div>
    </div>
  );
}
